import { keyframes } from '@emotion/react';
import { alpha } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

const TRACK_PERIODS = 3;
const COLUMN_COUNT = 4;
const COLUMN_MIN_WIDTH = 140;

const COLUMN_WIDTH = `max(${100 / COLUMN_COUNT}%, ${COLUMN_MIN_WIDTH}px)`;
const columnLeft = (index: number) => `calc(${index} * ${COLUMN_WIDTH})`;

export const PILL_OFFSETS = Array.from(
  { length: TRACK_PERIODS },
  (_, period) => `${(period * 100) / TRACK_PERIODS}%`,
);

const drift = keyframes({
  from: { transform: 'translate3d(0, 0, 0)' },
  to: { transform: `translate3d(0, -${100 / TRACK_PERIODS}%, 0)` },
});

interface ColumnParams {
  index: number;
  duration: string;
  delay: string;
  height: string;
  goesUp: boolean;
}

const useDecorativePatternStyles = makeStyles()((theme) => {
  const opaqueAtTop = `linear-gradient(${alpha(theme.palette.accent.dark, 0.05)}, ${alpha(theme.palette.accent.dark, 0)})`;
  const opaqueAtBottom = `linear-gradient(${alpha(theme.palette.accent.dark, 0)}, ${alpha(theme.palette.accent.dark, 0.05)})`;

  const buildColumn = ({ index, duration, delay, height, goesUp }: ColumnParams) => ({
    track: {
      position: 'absolute' as const,
      left: columnLeft(index),
      top: '-100%',
      width: COLUMN_WIDTH,
      height: `${TRACK_PERIODS * 100}%`,
      animation: `${drift} ${duration} linear infinite`,
      animationDelay: delay,
      animationDirection: goesUp ? ('normal' as const) : ('reverse' as const),
      willChange: 'transform',
      '@media (prefers-reduced-motion: reduce)': {
        animationPlayState: 'paused',
        willChange: 'auto',
      },
    },
    pill: {
      position: 'absolute' as const,
      left: 0,
      width: '100%',
      height,
      borderRadius: 9999,
      background: goesUp ? opaqueAtTop : opaqueAtBottom,
    },
  });

  const columns = [
    buildColumn({ index: 0, duration: '70s', delay: '0s', height: '20%', goesUp: true }),
    buildColumn({ index: 1, duration: '95s', delay: '-30s', height: '25%', goesUp: false }),
    buildColumn({ index: 2, duration: '80s', delay: '-55s', height: '23.33%', goesUp: true }),
    buildColumn({ index: 3, duration: '115s', delay: '-15s', height: '21.67%', goesUp: false }),
  ];

  return {
    root: {
      position: 'absolute' as const,
      inset: 0,
      overflow: 'hidden',
    },

    track0: columns[0].track,
    pill0: columns[0].pill,
    track1: columns[1].track,
    pill1: columns[1].pill,
    track2: columns[2].track,
    pill2: columns[2].pill,
    track3: columns[3].track,
    pill3: columns[3].pill,
  };
});

export default useDecorativePatternStyles;
